import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { useT } from "../../lib/i18n";

const KEY = "kouoh_astra";
const load = () => { try { return JSON.parse(sessionStorage.getItem(KEY)) || { sid: null, msgs: [] }; } catch { return { sid: null, msgs: [] }; } };

export function AstraWidget() {
  const { t, lang } = useT();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(load);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { sessionStorage.setItem(KEY, JSON.stringify(state)); endRef.current?.scrollIntoView({ block: "end" }); }, [state, open]);
  if (loc.pathname.startsWith("/admin") || loc.pathname.startsWith("/espace-client") || loc.pathname.startsWith("/demande/")) return null;

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput(""); setBusy(true);
    setState((s) => ({ ...s, msgs: [...s.msgs, { role: "user", content: text }, { role: "assistant", content: "" }] }));
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/assistant`, { method: "POST", headers: { "Content-Type": "application/json", "X-Requested-With": "kouoh" }, body: JSON.stringify({ session_id: state.sid, message: text, lang, page: loc.pathname }) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || res.statusText);
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      for (;;) {
        const { value, done } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.session_id) setState((s) => ({ ...s, sid: ev.session_id }));
          if (ev.delta) setState((s) => { const m = [...s.msgs]; m[m.length - 1] = { ...m[m.length - 1], content: m[m.length - 1].content + ev.delta }; return { ...s, msgs: m }; });
          if (ev.error) setState((s) => { const m = [...s.msgs]; m[m.length - 1] = { role: "assistant", content: ev.error }; return { ...s, msgs: m }; });
        }
      }
    } catch (e) {
      setState((s) => { const m = [...s.msgs]; m[m.length - 1] = { role: "assistant", content: t("Astra est momentanément indisponible. Le formulaire de demande reste le plus sûr.") }; return { ...s, msgs: m }; });
    } finally { setBusy(false); }
  };

  const linkify = (txt) => {
    const clean = txt.replace(/\[([^\]]+)\]\((\/[^)\s]+)\)/g, "$1 ($2)").replace(/\*\*([^*]+)\*\*/g, "$1");
    return clean.split(/(\/demande\/[a-z]+|\/pack\/[a-z_]+|\/atelier|\/realisations|\/demande)/g).map((part, i) => (/^\//.test(part) ? <Link key={i} to={part} className="underline" onClick={() => setOpen(false)}>{part}</Link> : part));
  };

  return (
    <div className="fixed bottom-5 right-5 z-40 hidden md:flex flex-col items-end gap-3" data-testid="astra-widget">
      {open && (
        <section className="w-[min(92vw,380px)] bg-[#FBF9F5] border border-[#1C1917] shadow-2xl flex flex-col max-h-[70vh]" role="dialog" aria-label="Astra, assistante de l'atelier" data-testid="astra-panel">
          <header className="flex items-center justify-between px-4 py-3 bg-[#1C1917] text-[#FBF9F5]">
            <p className="text-sm flex items-center gap-2"><Sparkles size={14} className="text-[#C25E38]" /> <strong>Astra</strong> <span className="opacity-70 text-xs">— {t("assistante de l'atelier")}</span></p>
            <button onClick={() => setOpen(false)} aria-label="Fermer" data-testid="astra-close"><X size={16} /></button>
          </header>
          <div className="flex-1 overflow-auto p-4 grid gap-3 content-start text-sm" data-testid="astra-messages">
            {!state.msgs.length && <p className="text-[#57534E]">{t("Bonjour, je suis Astra. Posez-moi vos questions sur les minimums, les délais, la broderie ou le DTF — et je vous guide vers la bonne demande.")}</p>}
            {state.msgs.map((m, i) => (
              <div key={i} className={`max-w-[88%] px-3 py-2 whitespace-pre-line ${m.role === "user" ? "self-end bg-[#1C1917] text-[#FBF9F5]" : "self-start bg-white border border-[#E8E2D8]"}`} data-testid={`astra-msg-${m.role}`}>{m.role === "assistant" ? (m.content ? linkify(m.content) : <span className="opacity-50">…</span>) : m.content}</div>
            ))}
            <div ref={endRef} />
          </div>
          <form className="flex gap-2 p-3 border-t border-[#E8E2D8]" onSubmit={(e) => { e.preventDefault(); send(); }}>
            <input className="k-input py-2 text-sm flex-1" value={input} onChange={(e) => setInput(e.target.value)} placeholder={t("Votre question…")} maxLength={1200} data-testid="astra-input" />
            <button type="submit" className="k-btn k-btn-sm px-3" disabled={busy || !input.trim()} aria-label="Envoyer" data-testid="astra-send"><Send size={14} /></button>
          </form>
          <p className="px-4 pb-3 text-[10px] text-[#8C857B]">{t("Réponses générées par IA, sans prix ni délai garantis. Le devis est établi par l'équipe.")} <Link to="/demande" className="underline" onClick={() => setOpen(false)}>{t("Demander un devis")}</Link></p>
        </section>
      )}
      <button onClick={() => setOpen(!open)} className="k-btn rounded-full px-5 py-3 shadow-xl" aria-expanded={open} data-testid="astra-toggle"><MessageCircle size={16} /> {open ? t("Fermer") : t("Une question ? Astra")}</button>
    </div>
  );
}
